import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ChartContainer } from '@/components/ui/chart';

export interface MoodDataPoint {
  month: string;
  moodScore: number;
  bookCount: number;
}

interface MoodArcChartProps {
  moodData: MoodDataPoint[];
}

export function MoodArcChart({ moodData }: MoodArcChartProps) {
  if (moodData.length === 0) return null;

  const chartConfig = {
    moodScore: {
      label: "Mood Score",
      color: "hsl(var(--primary))",
    },
  };

  // Calculate overall mood tendency
  const avgMood = moodData.reduce((sum, d) => sum + d.moodScore, 0) / moodData.length;
  const moodTendency = avgMood > 0.2 ? 'uplifting' : avgMood < -0.2 ? 'intense' : 'balanced';

  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          Reading Mood Journey
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-6">
          Emotional tone of your reading throughout the year - from dark/intense genres to light/uplifting ones
        </p>

        <ChartContainer config={chartConfig} className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={moodData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey="month" 
                stroke="hsl(var(--muted-foreground))"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                stroke="hsl(var(--muted-foreground))"
                domain={[-1, 1]}
                ticks={[-1, -0.5, 0, 0.5, 1]}
                tick={{ fontSize: 12 }}
                label={{ 
                  value: 'Mood Score', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fill: 'hsl(var(--muted-foreground))', fontSize: 12 }
                }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
                labelStyle={{ color: 'hsl(var(--popover-foreground))' }}
                formatter={(value: number, name: string, props: any) => {
                  const mood = value > 0.3 ? '😊 Uplifting' : value < -0.3 ? '🌙 Intense' : '⚖️ Balanced';
                  return [
                    `${mood} (${value.toFixed(2)})`,
                    `${props.payload.bookCount} books`
                  ];
                }}
              />
              <ReferenceLine 
                y={0} 
                stroke="hsl(var(--muted-foreground))" 
                strokeDasharray="3 3" 
                label={{ 
                  value: 'Neutral', 
                  position: 'right',
                  style: { fill: 'hsl(var(--muted-foreground))', fontSize: 11 }
                }}
              />
              <Line 
                type="monotone" 
                dataKey="moodScore" 
                stroke="hsl(var(--primary))" 
                strokeWidth={3}
                dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>

        <div className="mt-4 flex items-center justify-center gap-6 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary/20" />
            <span>Positive (Romance, Comedy, Fantasy)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-muted" />
            <span>Neutral (Fiction, Biography)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-accent/40" />
            <span>Intense (Horror, Thriller, Mystery)</span>
          </div>
        </div>

        <div className="mt-4 p-3 bg-muted/30 rounded-lg text-center">
          <p className="text-sm">
            Your reading this year was predominantly <span className="font-bold text-primary">{moodTendency}</span>
            {avgMood > 0.2 && ' - you gravitated toward uplifting and positive stories'}
            {avgMood < -0.2 && ' - you explored darker and more intense narratives'}
            {avgMood >= -0.2 && avgMood <= 0.2 && ' - you maintained a diverse mix of emotional tones'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
